/**
 * urlHashParams — shared primitives for binding state to the URL hash fragment.
 *
 * `useUrlHashState` and `useUrlHashRoute` both store `key=value` pairs inside
 * `location.hash` (`#a=1&b=2`); each hook owns exactly one key while leaving
 * the others untouched. The parse / serialize / read / commit primitives live
 * here so the two hooks share one implementation rather than each carrying a
 * private, drift-prone copy.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

/**
 * History-write mode for {@link commitHashKey}.
 *
 *   - `"replace"` — ephemeral in-demo state (an active level, panel, filter):
 *     overwrites the current entry so Back leaves the surrounding page.
 *   - `"push"` — a route change a visitor would bookmark or step through with
 *     the browser Back / Forward buttons: appends a history entry.
 */
export type HashHistoryMode = "push" | "replace";

/** Parse `#a=1&b=2` (with or without the leading `#`) into an `URLSearchParams`. */
export function parseHash(hash: string): URLSearchParams {
  const trimmed = hash.startsWith("#") ? hash.slice(1) : hash;
  return new URLSearchParams(trimmed);
}

/** Serialize a params map into a `#a=1&b=2` string (empty when no params). */
export function serializeHash(params: URLSearchParams): string {
  const str = params.toString();
  return str.length > 0 ? `#${str}` : "";
}

/** Read a single key from `location.hash`; `null` when absent or off-DOM. */
export function readHashKey(key: string): string | null {
  if (typeof window === "undefined") return null;
  return parseHash(window.location.hash).get(key);
}

/**
 * Write a single key into `location.hash`, preserving every other key.
 *
 * A `null` or empty `value` removes the key. `mode` selects the History API
 * call (`"replace"` vs `"push"` — see {@link HashHistoryMode}). Same-page
 * History API writes do not emit `hashchange` natively, so this helper emits
 * one after committing to keep sibling hook instances synchronized. No-op
 * when off-DOM (SSR / non-browser test environments).
 */
export function commitHashKey(key: string, value: string | null, mode: HashHistoryMode): void {
  if (typeof window === "undefined") return;
  const params = parseHash(window.location.hash);
  if (value === null || value === "") {
    params.delete(key);
  } else {
    params.set(key, value);
  }
  const target = `${window.location.pathname}${window.location.search}${serializeHash(params)}`;
  if (mode === "push") {
    window.history.pushState(null, "", target);
  } else {
    window.history.replaceState(null, "", target);
  }
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}
